import React from "react";
import { Button } from "@blueprintjs/core";
import HtmlDefault from "common/fragments/HtmlDefault";
import { ButtonView } from "common/views/button/Button.view";
import HeadingView from "common/views/heading/Heading.view";
import { ImageVariant, ImageView } from "common/views/image/Image.view";
import PageLinkView from "common/views/pageLink/PageLink.view";
import TypographyView from "common/views/typographyView/Typography.view";
import { Fragment } from "core/components/FragmentProvider";
import Label from "core/components/label/Label";
import constants from "../constants";
import { Article } from "./entities";
import { ArticleTypeRegistry } from "./IArticleType";
import { IconNames } from "@blueprintjs/icons";
import * as articleActions from "./arcticleActions";
import Query from "core/api/QueryComponent";

interface IArticleEditFragmentProps {
	article: Article;
	onChange: (article: Article) => void;
	uiSchema: any;
	actions?: typeof articleActions;
	contributorDisplay?: JSX.Element;
	attachmentDisplay?: JSX.Element;
}

export const title: Fragment<IArticleEditFragmentProps> = {
	name: "Title",
	component: props => (
		<HeadingView viewKey="article.title">
			{props.article.displayName || props.article.name}
		</HeadingView>
	)
};

export const description: Fragment<IArticleEditFragmentProps> = {
	name: "Description",
	enabled: props => !!props.article.description,
	component: props => (
		<TypographyView viewKey="article.description">
			<HtmlDefault html={props.article.description} />
		</TypographyView>
	)
};

export const icon: Fragment<IArticleEditFragmentProps> = {
	name: "Icon",
	component: props => (
		<ImageView
			viewKey="article.icon"
			variant={ImageVariant.ICON}
			src={props.article.iconUrl}
		/>
	)
};

export const tags: Fragment<IArticleEditFragmentProps> = {
	name: "Tags",
	enabled: props => !!props.article.tags && props.article.tags.length > 0,
	component: props => (
		<div>
			<HeadingView viewKey="property.title">
				<Label k="tags" v="Tags" />
			</HeadingView>
			{props.article.tags.reduce((lst, tag, i) => {
				if (i > 0) {
					lst.push(
						<React.Fragment key={tag.id + "-separator"}>
							{", "}
						</React.Fragment>
					);
				}

				lst.push(
					<PageLinkView
						key={tag.id}
						viewKey="tag.link"
						preserveParams={false}
						params={{ [constants.PARAM_TAG_IDS]: tag.id }}
					>
						<Label k={`Tag.${tag.id}.name`} v={tag.name} />
					</PageLinkView>
				);

				return lst;
			}, [])}
		</div>
	)
};

export const categories: Fragment<IArticleEditFragmentProps> = {
	name: "Categories",
	enabled: props =>
		!!props.article.categories && props.article.categories.length > 0,
	component: props => (
		<div>
			<HeadingView viewKey="property.title">
				<Label k="kb.categories" v="Categories" />
			</HeadingView>
			{props.article.categories.reduce((lst, c, i) => {
				if (i > 0) {
					lst.push(
						<React.Fragment key={c.id + "-separator"}>
							{", "}
						</React.Fragment>
					);
				}

				lst.push(
					<PageLinkView
						key={c.id}
						viewKey="category.link"
						preserveParams={true}
						params={{
							[constants.PARAM_CATEGORY_ID]: c.id
						}}
					>
						{c.name}
					</PageLinkView>
				);

				return lst;
			}, [])}
		</div>
	)
};

export const bookmark: Fragment<IArticleEditFragmentProps> = {
	name: "Bookmark Controls",

	component: props => (
		<Query.Many entity={Article} args={{ id: props.article.id }}>
			{({ data, forceRefresh }) => {
				if (!Array.isArray(data) || data.length === 0) {
					return;
				}

				return (
					<ButtonView viewKey="article.bookmark">
						<Button
							style={{ float: "right" }}
							small={true}
							icon={
								!data[0].playlist
									? IconNames.STAR_EMPTY
									: IconNames.BOOKMARK
							}
							onClick={() => {
								props.actions.toggleBookMark(
									props.article.id,
									forceRefresh
								);
							}}
						/>
						{/*<Label k="article.bookmark" v="Bookmark" />*/}
					</ButtonView>
				);
			}}
		</Query.Many>
	)
};

export const content: Fragment<IArticleEditFragmentProps> = {
	name: "Content",
	component: props => {
		const articleType =
			(props.article.uiSchema &&
				ArticleTypeRegistry.getObject(props.article.uiSchema.type)) ||
			null;

		return (
			(articleType &&
				React.createElement(articleType.component, props)) ||
			null
		);
	}
};



// WEBPACK FOOTER //
// ./src/panels/kb/article/fragments.tsx